import React, { useState, useRef, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { chat, searchProjects } from '../../../services/aiService';
import { getResource as getSupplementaryResource, getResourceByUrl } from '../../../services/aiDocumentService';
import { getResource as getSystemDocumentResource } from '../../../services/aiSystemDocumentService';
import * as alert from '../../../redux/alertSnackBarRedux';
import Logo from '../../../assets/img/aes-logo-white.png';
import { getDocumentIcon } from './documentIcon';
import AssistantMarkdown from './AssistantMarkdown';
import AssistantClarificationChoices from './AssistantClarificationChoices';
import { AssistantResponseLayout, shouldShowResponseCopy } from './AssistantResponseCopy';
import { STARTER_ACTIONS } from './chatStarterActions';
import {
  buildChatHistory,
  canSendChatMessage,
  clearSavedMessages,
  getSavedMessages,
  recordDiagnosticResponse,
  saveMessages,
  shouldSendChatMessageOnKeyDown,
} from './chatConversation';

const PRIMARY = 'var(--primary, #3699FF)';
const PRIMARY_HOVER = 'var(--primary-hover, var(--primary, #187DE4))';
const PANEL_SIZE_STORAGE_KEY = 'aes-ai-chat-panel-size';
const DESKTOP_MEDIA_QUERY = '(min-width: 992px)';
const MIN_PANEL_WIDTH = 340;
const MIN_PANEL_HEIGHT = 420;
const PROJECT_SEARCH_DEBOUNCE_MS = 250;
const PROJECT_SEARCH_LIMIT = 8;
const getActiveProjectMention = (value, cursorPosition) => {
  const textBeforeCursor = value.slice(0, cursorPosition);
  const mentionMatch = textBeforeCursor.match(/(^|\s)@([^\s@]*)$/);

  if (!mentionMatch) {
    return null;
  }

  const matchedText = mentionMatch[0];
  const atOffset = matchedText.lastIndexOf('@');
  const startIndex = cursorPosition - matchedText.length + atOffset;

  return {
    query: mentionMatch[2] || '',
    startIndex,
    endIndex: cursorPosition,
  };
};

const replaceTextareaSelection = (textarea, replacement, startIndex, endIndex) => {
  if (!textarea) {
    return false;
  }

  textarea.focus();
  textarea.setSelectionRange(startIndex, endIndex);

  try {
    return document.execCommand('insertText', false, replacement);
  } catch {
    return false;
  }
};

const clampPanelSize = (width, height) => {
  const maxWidth = Math.max(0, window.innerWidth - 32);
  const maxHeight = Math.max(0, window.innerHeight - 32);
  const minWidth = Math.min(MIN_PANEL_WIDTH, maxWidth);
  const minHeight = Math.min(MIN_PANEL_HEIGHT, maxHeight);

  return {
    width: Math.min(Math.max(width, minWidth), maxWidth),
    height: Math.min(Math.max(height, minHeight), maxHeight),
  };
};

const getSavedPanelSize = () => {
  try {
    const savedSize = JSON.parse(localStorage.getItem(PANEL_SIZE_STORAGE_KEY));

    if (!Number.isFinite(savedSize?.width) || !Number.isFinite(savedSize?.height)) {
      return null;
    }

    return clampPanelSize(savedSize.width, savedSize.height);
  } catch {
    return null;
  }
};

const savePanelSize = (panelSize) => {
  try {
    localStorage.setItem(PANEL_SIZE_STORAGE_KEY, JSON.stringify(panelSize));
  } catch {
    return;
  }
};

const styles = {
  toggle: {
    borderRadius: '50%',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  toggleLogo: {
    display: 'block',
    width: '80%',
    height: 'auto',
  },
  panel: {
    background: '#fff',
    borderRadius: '6px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
    border: '1px solid #E4E6EF',
    overflow: 'hidden',
  },
  header: {
    background: PRIMARY,
    color: '#fff',
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerLogo: {
    display: 'block',
    height: '40px',
    width: 'auto',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: 1,
  },
  headerTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '4px',
    columnGap: '10px',
  },
  knowledgeBaseLink: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '12px',
    fontWeight: 500,
    textDecoration: 'none',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    borderRadius: '4px',
    padding: '1px 4px',
    transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
  },
  headerClose: {
    marginLeft: 'auto',
    flex: '0 0 auto',
    background: 'transparent',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '18px',
    opacity: 0.9,
    lineHeight: 1,
    padding: '4px',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    background: '#F5F8FA',
  },
  empty: {
    color: '#7E8299',
    fontSize: '14px',
    textAlign: 'center',
    margin: 'auto',
    lineHeight: 1.6,
    maxWidth: '310px',
    width: '100%',
  },
  emptyTitle: {
    margin: '0 0 5px',
    color: '#3F4254',
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: 1.3,
    userSelect: 'none',
  },
  emptyBody: {
    margin: '0 auto',
    color: '#7E8299',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.5,
    maxWidth: '310px',
    userSelect: 'none',
  },
  suggestion: {
    maxWidth: '100%',
    padding: '5px 8px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '11px',
    lineHeight: 1.35,
    textAlign: 'left',
    whiteSpace: 'normal',
  },
  userMessage: {
    alignSelf: 'flex-end',
    background: PRIMARY,
    color: '#fff',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '13px',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  responseMessage: {
    alignSelf: 'flex-start',
    background: '#fff',
    color: '#3F4254',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '13px',
    lineHeight: 1.55,
    wordBreak: 'break-word',
  },
  inputRow: {
    padding: '10px',
    borderTop: '1px solid #E4E6EF',
    background: '#fff',
  },
  input: {
    display: 'block',
    width: '100%',
    minHeight: '40px',
    maxHeight: '140px',
    padding: '8px 10px 2px',
    border: 'none',
    background: 'transparent',
    boxSizing: 'border-box',
    fontSize: '13px',
    lineHeight: 1.45,
    outline: 'none',
    overflowY: 'hidden',
    resize: 'none',
  },
  sendBtn: {
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '9px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    minHeight: '36px',
  },
  disabledMessage: {
    alignSelf: 'flex-start',
    background: '#FFF8DD',
    color: '#7A6400',
    padding: '9px 12px',
    borderRadius: '6px',
    fontSize: '13px',
  },
  sources: {
    maxWidth: '100%',
    width: '100%',
  },
  sourcesHeader: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#7E8299',
    margin: '2px 0 5px',
  },
  sourceLink: {
    fontSize: '11px',
    color: PRIMARY,
    textDecoration: 'none',
    wordBreak: 'break-all',
  },
  sourceList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  sourceActionLayout: {
    maxWidth: '100%',
    fontFamily: 'inherit',
    overflowWrap: 'anywhere',
  },
  sourceActionBusy: {
    cursor: 'wait',
    opacity: 0.6,
    pointerEvents: 'none',
  },
  sourceIcon: {
    flex: '0 0 auto',
    width: '14px',
    height: '14px',
    marginRight: '6px',
  },
};

function normalizeSource(source) {
  if (typeof source === 'string') {
    const parsedSource = parseSource(source);

    return {
      documentId: null,
      title: parsedSource.name,
      fileName: null,
      sourceType: parsedSource.type,
      sourceUrl: parsedSource.url,
      projectNumber: parsedSource.project,
      resourceUrl: parsedSource.url,
      jobUrl: null,
      proposalUrl: null,
      knowledgeBaseKind: null,
      sourceKind: null,
      sourceDocumentId: null,
    };
  }

  return {
    documentId: source.documentId || null,
    title: source.title || source.fileName || source.sourceUrl || 'Source',
    fileName: source.fileName || null,
    sourceType: source.sourceType || null,
    sourceUrl: source.sourceUrl || null,
    projectNumber: source.projectNumber || null,
    resourceUrl: source.resourceUrl || null,
    jobUrl: source.jobUrl || null,
    proposalUrl: source.proposalUrl || null,
    knowledgeBaseKind: source.knowledgeBaseKind ?? null,
    sourceKind: source.sourceKind ?? null,
    sourceDocumentId: source.sourceDocumentId ?? null,
  };
}

const isPositiveIdentifier = (value) => Number.isInteger(Number(value)) && Number(value) > 0;

const hasAuthenticatedResource = (source) => {
  if (/^\/?api\//i.test(source.resourceUrl || '')) return true;

  const isSystemDocument = Number(source.knowledgeBaseKind) === 1;
  if (isSystemDocument && isPositiveIdentifier(source.sourceKind) && isPositiveIdentifier(source.sourceDocumentId)) return true;

  const isSupplementaryDocument = Number(source.knowledgeBaseKind) === 2 || (source.sourceType || '').toLowerCase() === 'upload';
  return isSupplementaryDocument && isPositiveIdentifier(source.documentId);
};

const loadAuthenticatedResource = (source) => {
  if (/^\/?api\//i.test(source.resourceUrl || '')) return getResourceByUrl(source.resourceUrl);

  if (Number(source.knowledgeBaseKind) === 1 && isPositiveIdentifier(source.sourceKind) && isPositiveIdentifier(source.sourceDocumentId)) {
    return getSystemDocumentResource(source.sourceKind, source.sourceDocumentId);
  }

  const isSupplementaryDocument = Number(source.knowledgeBaseKind) === 2 || (source.sourceType || '').toLowerCase() === 'upload';
  return isSupplementaryDocument && isPositiveIdentifier(source.documentId) ? getSupplementaryResource(source.documentId) : null;
};

const getSourceKey = (source, index) =>
  source.resourceUrl || `${source.knowledgeBaseKind || 'source'}-${source.sourceKind || 'kind'}-${source.sourceDocumentId || source.documentId || index}`;

const normalizeSourceLabel = (value) =>
  (value || '')
    .replace(/[*_`]/g, '')
    .replace(/^['"]|['"]$/g, '')
    .trim()
    .toLowerCase();

const removeDuplicateSourceLines = (text, sources) => {
  const sourceLabels = new Set();

  (Array.isArray(sources) ? sources : []).forEach((source) => {
    const parsedSource = normalizeSource(source);
    [parsedSource.title, parsedSource.fileName].forEach((label) => {
      const normalizedLabel = normalizeSourceLabel(label);
      if (normalizedLabel) sourceLabels.add(normalizedLabel);
    });
  });

  if (!sourceLabels.size) return text;

  return (text || '')
    .split(/\r?\n/)
    .filter((line) => {
      const sourceLine = line.match(/^\s*(?:[-*]\s*)?(?:\*\*)?sources?(?:\*\*)?\s*:\s*(.+?)\s*$/i);
      return !sourceLine || !sourceLabels.has(normalizeSourceLabel(sourceLine[1]));
    })
    .join('\n');
};

function parseSource(raw) {
  const text = (raw || '').trim();

  const urlMatch = text.match(/https?:\/\/[^\s)]+/);
  if (urlMatch) {
    const url = urlMatch[0].replace(/[).]+$/, '');
    let name = text.replace(/\s*\(?\s*https?:\/\/.*$/, '').trim();

    if (!name) {
      name = url;
    }

    return { type: 'web', name, url, project: null };
  }

  const project = (text.match(/\d{2}-\d{4}-\d{2}/) || [])[0] || null;

  let label = text;

  if (project) {
    const duplicateIndex = text.indexOf(' (' + project);

    if (duplicateIndex > 0) {
      label = text.slice(0, duplicateIndex).trim();
    }
  }

  let name = label;
  const dashIndex = label.indexOf(' — ');

  if (dashIndex >= 0) {
    name = label.slice(dashIndex + 3).trim();
  } else if (project) {
    name =
      label
        .replace(project, '')
        .replace(/^\s*[/—-]\s*/, '')
        .trim() || label;
  }

  return {
    type: 'document',
    name: name || label,
    url: null,
    project,
  };
}

function prettyUrl(url) {
  try {
    const parsedUrl = new URL(url);
    const path = parsedUrl.pathname.replace(/\/$/, '');

    return parsedUrl.hostname.replace(/^www\./, '') + path;
  } catch {
    return url;
  }
}

function AnswerContent({ text, sources }) {
  return <AssistantMarkdown>{removeDuplicateSourceLines(text, sources)}</AssistantMarkdown>;
}

const normalizeApplicationAction = (action) => {
  if (!action || typeof action !== 'object') {
    return null;
  }

  const label = action.label || action.title || action.text || action.displayText;
  const url = action.url || action.route || action.href || action.path || action.targetUrl;

  if (typeof label !== 'string' || typeof url !== 'string' || /^javascript:/i.test(url.trim())) {
    return null;
  }

  return {
    label: label.trim(),
    url: url.trim(),
  };
};

const hasApplicationActions = (actions) => Array.isArray(actions) && actions.some((action) => Boolean(normalizeApplicationAction(action)));

const hasSources = (sources) => Array.isArray(sources) && sources.length > 0;

const ApplicationActions = ({ actions }) => {
  const applicationActions = (Array.isArray(actions) ? actions : []).map(normalizeApplicationAction).filter(Boolean);

  if (!applicationActions.length) {
    return null;
  }

  return (
    <div className='aes-ai-chat-application-actions'>
      <div className='aes-ai-chat-application-actions-header'>Related AES Records</div>
      <div className='aes-ai-chat-application-actions-list'>
        {applicationActions.map((action, index) =>
          /^https?:\/\//i.test(action.url) ? (
            <a
              key={`${action.url}-${index}`}
              className='aes-ai-chat-application-action'
              href={action.url}
              target='_blank'
              rel='noopener noreferrer'
            >
              {action.label}
            </a>
          ) : (
            <Link key={`${action.url}-${index}`} className='aes-ai-chat-application-action' to={action.url}>
              {action.label}
            </Link>
          ),
        )}
      </div>
    </div>
  );
};

const AssistantSources = ({ sources, openingSourceKey, onOpenSource }) => {
  if (!Array.isArray(sources) || !sources.length) {
    return null;
  }

  return (
    <div className='aes-ai-chat-sources' style={styles.sources}>
      <div className='aes-ai-chat-sources-header'>Sources</div>
      <div className='aes-ai-chat-sources-list' style={styles.sourceList}>
        {sources.map((source, sourceIndex) => {
          const parsedSource = normalizeSource(source);
          const sourceKey = getSourceKey(parsedSource, sourceIndex);
          const isWebsite = parsedSource.sourceType === 'website' || /^https?:\/\//i.test(parsedSource.resourceUrl || '');
          const canOpenResource = hasAuthenticatedResource(parsedSource);
          const SourceIcon = getDocumentIcon(parsedSource.title || parsedSource.fileName);
          const sourceContent = (
            <>
              <SourceIcon className='aes-ai-chat-source__icon' style={styles.sourceIcon} aria-hidden='true' focusable='false' />
              <span>{parsedSource.title}</span>
            </>
          );

          return (
            <div key={sourceKey} className='aes-ai-chat-source'>
              {isWebsite && parsedSource.resourceUrl ? (
                <a
                  className='aes-ai-chat-application-action'
                  style={styles.sourceActionLayout}
                  href={parsedSource.resourceUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {sourceContent}
                </a>
              ) : canOpenResource ? (
                <button
                  type='button'
                  className='aes-ai-chat-application-action'
                  style={openingSourceKey === sourceKey ? { ...styles.sourceActionLayout, ...styles.sourceActionBusy } : styles.sourceActionLayout}
                  disabled={openingSourceKey === sourceKey}
                  onClick={() => onOpenSource(parsedSource, sourceKey)}
                >
                  {sourceContent}
                </button>
              ) : (
                <div className='aes-ai-chat-source__title'>
                  {sourceContent}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

function AiChatPanel({ showAlert, userId }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => getSavedMessages(sessionStorage, userId));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_MEDIA_QUERY).matches);
  const [panelSize, setPanelSize] = useState(getSavedPanelSize);
  const [projectMention, setProjectMention] = useState(null);
  const [projectResults, setProjectResults] = useState([]);
  const [projectSearchLoading, setProjectSearchLoading] = useState(false);
  const [projectSearchError, setProjectSearchError] = useState(false);
  const [highlightedProjectIndex, setHighlightedProjectIndex] = useState(0);
  const [openingSourceKey, setOpeningSourceKey] = useState('');
  const isSending = useRef(false);
  const bottomRef = useRef(null);
  const panelRef = useRef(null);
  const composerRef = useRef(null);
  const textareaRef = useRef(null);
  const projectSearchRequestId = useRef(0);
  const projectPickerRef = useRef(null);
  const resizeOperation = useRef(null);

  const clearProjectPicker = useCallback(() => {
    projectSearchRequestId.current += 1;
    setProjectMention(null);
    setProjectResults([]);
    setProjectSearchLoading(false);
    setProjectSearchError(false);
    setHighlightedProjectIndex(0);
  }, []);

  const updateProjectMention = useCallback((value, cursorPosition) => {
    const activeMention = getActiveProjectMention(value, cursorPosition);

    setProjectMention(activeMention);
    setHighlightedProjectIndex(0);

    if (!activeMention) {
      projectSearchRequestId.current += 1;
      setProjectResults([]);
      setProjectSearchLoading(false);
      setProjectSearchError(false);
    }
  }, []);

  const stopResize = useCallback(() => {
    const operation = resizeOperation.current;

    if (!operation) {
      return;
    }

    resizeOperation.current = null;
    document.body.style.userSelect = operation.bodyUserSelect;
    document.body.style.cursor = operation.bodyCursor;

    setPanelSize((currentSize) => {
      if (currentSize) {
        const clampedSize = clampPanelSize(currentSize.width, currentSize.height);
        savePanelSize(clampedSize);
        return clampedSize;
      }

      return currentSize;
    });
  }, []);

  const handleResizeMove = useCallback((event) => {
    const operation = resizeOperation.current;

    if (!operation) {
      return;
    }

    event.preventDefault();

    const width = operation.resizeWidth ? operation.width - (event.clientX - operation.clientX) : operation.width;
    const height = operation.resizeHeight ? operation.height - (event.clientY - operation.clientY) : operation.height;

    setPanelSize(clampPanelSize(width, height));
  }, []);

  const startResize = (event, resizeWidth, resizeHeight) => {
    if (!isDesktop || !panelRef.current || event.button !== 0) {
      return;
    }

    event.preventDefault();

    const panelBounds = panelRef.current.getBoundingClientRect();

    resizeOperation.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      width: panelBounds.width,
      height: panelBounds.height,
      resizeWidth,
      resizeHeight,
      bodyUserSelect: document.body.style.userSelect,
      bodyCursor: document.body.style.cursor,
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = resizeWidth && resizeHeight ? 'nwse-resize' : resizeWidth ? 'ew-resize' : 'ns-resize';
  };

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  useEffect(() => {
    saveMessages(sessionStorage, userId, messages);
  }, [messages, userId]);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      if (!textareaRef.current) {
        return;
      }

      textareaRef.current.style.height = 'auto';

      const textareaHeight = Math.min(Math.max(textareaRef.current.scrollHeight, 40), 140);
      textareaRef.current.style.height = `${textareaHeight}px`;
      textareaRef.current.style.overflowY = textareaRef.current.scrollHeight > 140 ? 'auto' : 'hidden';
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [input, open]);

  useEffect(() => {
    if (!projectMention) {
      return;
    }

    const requestId = ++projectSearchRequestId.current;
    setProjectSearchLoading(true);
    setProjectSearchError(false);

    const debounceTimer = setTimeout(async () => {
      try {
        const response = await searchProjects(projectMention.query, PROJECT_SEARCH_LIMIT);

        if (requestId !== projectSearchRequestId.current) {
          return;
        }

        const projects = response?.data ?? response ?? [];
        setProjectResults(Array.isArray(projects) ? projects : []);
        setHighlightedProjectIndex(0);
        setProjectSearchLoading(false);
      } catch (error) {
        if (requestId !== projectSearchRequestId.current) {
          return;
        }

        console.error('Unable to search AES projects.', error);
        setProjectResults([]);
        setProjectSearchLoading(false);
        setProjectSearchError(true);
      }
    }, PROJECT_SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(debounceTimer);

      if (requestId === projectSearchRequestId.current) {
        projectSearchRequestId.current += 1;
      }
    };
  }, [projectMention]);

  useEffect(() => {
    if (!projectMention) {
      return;
    }

    const handleOutsidePointerDown = (event) => {
      if (!composerRef.current?.contains(event.target)) {
        clearProjectPicker();
      }
    };

    document.addEventListener('pointerdown', handleOutsidePointerDown);

    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown);
  }, [projectMention, clearProjectPicker]);

  useEffect(() => {
    if (!projectPickerRef.current || !projectResults.length) {
      return;
    }

    const activeResult = projectPickerRef.current.querySelector(`[data-project-index="${highlightedProjectIndex}"]`);

    if (activeResult) {
      activeResult.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedProjectIndex, projectResults]);

  useEffect(() => {
    if (!open) {
      clearProjectPicker();
    }
  }, [open, clearProjectPicker]);

  useEffect(() => {
    const desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleBreakpointChange = (event) => {
      setIsDesktop(event.matches);

      if (!event.matches) {
        stopResize();
      }
    };

    desktopMediaQuery.addListener(handleBreakpointChange);

    return () => desktopMediaQuery.removeListener(handleBreakpointChange);
  }, [stopResize]);

  useEffect(() => {
    if (!open || !isDesktop || panelSize || !panelRef.current) {
      return;
    }

    const panelBounds = panelRef.current.getBoundingClientRect();
    setPanelSize(clampPanelSize(panelBounds.width, panelBounds.height));
  }, [open, isDesktop, panelSize]);

  useEffect(() => {
    const handleViewportResize = () => {
      if (!window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
        return;
      }

      setPanelSize((currentSize) => {
        if (!currentSize) {
          return currentSize;
        }

        const clampedSize = clampPanelSize(currentSize.width, currentSize.height);
        savePanelSize(clampedSize);
        return clampedSize;
      });
    };

    window.addEventListener('resize', handleViewportResize);
    window.addEventListener('pointermove', handleResizeMove);
    window.addEventListener('pointerup', stopResize);
    window.addEventListener('pointercancel', stopResize);

    return () => {
      window.removeEventListener('resize', handleViewportResize);
      window.removeEventListener('pointermove', handleResizeMove);
      window.removeEventListener('pointerup', stopResize);
      window.removeEventListener('pointercancel', stopResize);
      const operation = resizeOperation.current;

      if (operation) {
        document.body.style.userSelect = operation.bodyUserSelect;
        document.body.style.cursor = operation.bodyCursor;
        resizeOperation.current = null;
      }
    };
  }, [handleResizeMove, stopResize]);

  const selectProject = useCallback(
    (project) => {
      if (!projectMention || !project?.projectNumber) {
        return;
      }

      const projectText = `project ${project.projectNumber}`;
      const nextInput = input.slice(0, projectMention.startIndex) + projectText + input.slice(projectMention.endIndex);
      const cursorPosition = projectMention.startIndex + projectText.length;
      const textarea = textareaRef.current;
      const replacedNatively = replaceTextareaSelection(textarea, projectText, projectMention.startIndex, projectMention.endIndex);

      setInput(replacedNatively && textarea ? textarea.value : nextInput);
      clearProjectPicker();

      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
    },
    [input, projectMention, clearProjectPicker],
  );

  const handleSend = useCallback(async (selectedQuestion, clarificationOption) => {
    const question = typeof selectedQuestion === 'string' ? selectedQuestion : input;

    if (!question.trim() || isSending.current) {
      return;
    }

    isSending.current = true;
    clearProjectPicker();

    const userMessage = {
      role: 'user',
      content: question,
      createdAt: new Date().toISOString(),
    };

    setMessages((previousMessages) => [...previousMessages, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const history = buildChatHistory(messages);
      const data = await chat(question, history, clarificationOption);
      const createdAt = new Date().toISOString();
      const assistantMessage = {
        role: 'assistant',
        content: data.answer,
        correlationId: data.correlationId,
        createdAt,
        sources: data.sources || [],
        actions: data.actions || [],
        clarification: data.clarification,
      };

      recordDiagnosticResponse(sessionStorage, userId, {
        correlationId: data.correlationId,
        question,
        createdAt,
      });

      setMessages((previousMessages) => [
        ...previousMessages,
        assistantMessage,
      ]);
    } catch (error) {
      const status = error?.response?.status;
      const content =
        status === 503
          ? 'The assistant is currently unavailable.'
          : status === 429
          ? 'The service is busy. Please wait a moment and try again.'
          : 'Something went wrong. Please try again.';

      setMessages((previousMessages) => [
        ...previousMessages,
        {
          role: 'assistant',
          content,
          disabled: status === 503,
          transient: true,
        },
      ]);
    } finally {
      setLoading(false);
      isSending.current = false;
    }
  }, [input, messages, clearProjectPicker]);

  const handleKeyDown = (event) => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (projectMention && event.key === 'Escape') {
      event.preventDefault();
      clearProjectPicker();
      return;
    }

    if (projectMention && !event.shiftKey) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();

        if (projectResults.length) {
          setHighlightedProjectIndex((currentIndex) => (currentIndex + 1) % projectResults.length);
        }

        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();

        if (projectResults.length) {
          setHighlightedProjectIndex((currentIndex) => (currentIndex - 1 + projectResults.length) % projectResults.length);
        }

        return;
      }

      if ((event.key === 'Enter' || event.key === 'Tab') && projectResults.length) {
        event.preventDefault();
        selectProject(projectResults[highlightedProjectIndex]);
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();

      if (shouldSendChatMessageOnKeyDown(event, loading)) {
        handleSend();
      }
    }
  };

  const handleTextareaChange = (event) => {
    const nextInput = event.target.value;

    setInput(nextInput);
    updateProjectMention(nextInput, event.target.selectionStart);
  };

  const handleTextareaClick = (event) => {
    updateProjectMention(event.currentTarget.value, event.currentTarget.selectionStart);
  };

  const handleTextareaKeyUp = (event) => {
    if (['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'].includes(event.key)) {
      return;
    }

    updateProjectMention(event.currentTarget.value, event.currentTarget.selectionStart);
  };

  const applySuggestion = (suggestion, opensProjectPicker = false) => {
    setInput(suggestion);

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(suggestion.length, suggestion.length);

        if (opensProjectPicker) {
          updateProjectMention(suggestion, suggestion.length);
        } else {
          clearProjectPicker();
        }
      }
    });
  };

  const handleComposerClick = (event) => {
    if (event.target.closest('button, a, input, textarea, select')) {
      return;
    }

    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setInput('');
    clearProjectPicker();
    clearSavedMessages(sessionStorage);
  };

  const openSource = async (source, sourceKey) => {
    if (openingSourceKey) return;

    const resourceRequest = loadAuthenticatedResource(source);
    if (!resourceRequest) {
      showAlert({ message: 'This source document is not available to open.', variant: 'error' });
      return;
    }

    const documentWindow = window.open('', '_blank');
    setOpeningSourceKey(sourceKey);

    try {
      const response = await resourceRequest;
      const data = response?.data ?? response;
      const contentType = response?.headers?.['content-type'] || data?.type || 'application/pdf';
      const blob = data instanceof Blob ? data : new Blob([data], { type: contentType });
      const resourceUrl = URL.createObjectURL(blob);

      if (!documentWindow) {
        URL.revokeObjectURL(resourceUrl);
        throw new Error('Your browser blocked the document window.');
      }

      documentWindow.location.href = resourceUrl;
      setTimeout(() => URL.revokeObjectURL(resourceUrl), 60000);
    } catch (error) {
      if (documentWindow) documentWindow.close();
      showAlert({ message: error?.message || 'The source document could not be opened.', variant: 'error' });
    } finally {
      setOpeningSourceKey('');
    }
  };

  return (
    <>
      {!open && (
        <button
          className='aes-ai-chat-toggle'
          style={styles.toggle}
          onClick={() => setOpen(true)}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = PRIMARY_HOVER;
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = PRIMARY;
          }}
          title='AES Assistant'
          aria-label='Open AES Assistant'
          aria-expanded='false'
          aria-controls='aes-ai-chat-panel'
        >
          <img alt='' src={Logo} style={styles.toggleLogo} aria-hidden='true' />
        </button>
      )}

      {open && (
        <div
          ref={panelRef}
          id='aes-ai-chat-panel'
          className='aes-ai-chat-panel'
          style={{
            ...styles.panel,
            ...(isDesktop && panelSize ? { width: `${panelSize.width}px`, height: `${panelSize.height}px` } : {}),
          }}
          role='dialog'
          aria-label='AES Assistant'
        >
          {isDesktop && (
            <>
              <div
                className='aes-ai-chat-resize-handle aes-ai-chat-resize-handle--left'
                onPointerDown={(event) => startResize(event, true, false)}
                aria-hidden='true'
              />
              <div
                className='aes-ai-chat-resize-handle aes-ai-chat-resize-handle--top'
                onPointerDown={(event) => startResize(event, false, true)}
                aria-hidden='true'
              />
              <button
                type='button'
                className='aes-ai-chat-resize-handle aes-ai-chat-resize-handle--corner'
                onPointerDown={(event) => startResize(event, true, true)}
                aria-label='Resize chat panel'
              >
                <svg
                  className='aes-ai-chat-resize-icon'
                  viewBox='0 0 10 10'
                  fill='currentColor'
                  xmlns='http://www.w3.org/2000/svg'
                  aria-hidden='true'
                >
                  <path d='M8.82056 0.201012L0.201012 8.82056C-0.0670038 9.08857 -0.0670039 9.52311 0.201011 9.79113L0.207604 9.79772C0.475619 10.0657 0.910158 10.0657 1.17817 9.79772L9.79772 1.17817C10.0657 0.910158 10.0657 0.475619 9.79772 0.207603L9.79113 0.201011C9.52311 -0.0670041 9.08857 -0.0670036 8.82056 0.201012Z' />
                  <path d='M8.82286 4.23115L4.23193 8.82208C3.96513 9.08888 3.96513 9.52145 4.23193 9.78825L4.24358 9.7999C4.51038 10.0667 4.94295 10.0667 5.20975 9.7999L9.80068 5.20897C10.0675 4.94217 10.0675 4.5096 9.80068 4.2428L9.78903 4.23115C9.52223 3.96435 9.08966 3.96435 8.82286 4.23115Z' />
                  <path d='M9.79692 8.12817C10.0612 8.39247 10.0653 8.82581 9.80701 9.09399C9.78433 9.11751 9.66598 9.23632 9.45198 9.45043C9.23787 9.66453 9.11906 9.78293 9.09555 9.80562C8.82737 10.0639 8.39402 10.06 8.12973 9.79567C7.86528 9.53138 7.86139 9.09803 8.11963 8.8297C8.14221 8.80618 8.26056 8.68737 8.47466 8.47327C8.68867 8.25916 8.80748 8.14081 8.8311 8.11823C9.09928 7.86 9.53262 7.86388 9.79692 8.12817Z' />
                </svg>
              </button>
            </>
          )}
          <div className='aes-ai-chat-header' style={styles.header}>
            <img alt='AES' src={Logo} style={styles.headerLogo} />

            <div style={styles.headerTitleGroup}>
              <div style={styles.headerTitle}>AES Assistant</div>
              <Link
                to='/management/ai-documents'
                style={styles.knowledgeBaseLink}
                title='Manage AI Knowledge Base'
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = 'rgba(255, 255, 255, 0.14)';
                  event.currentTarget.style.borderColor = '#fff';
                  event.currentTarget.style.color = '#fff';
                  event.currentTarget.style.textDecoration = 'none';
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = 'transparent';
                  event.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                  event.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                  event.currentTarget.style.textDecoration = 'none';
                }}
              >
                AI Knowledge Base
              </Link>
            </div>

            <button
              style={styles.headerClose}
              onClick={() => setOpen(false)}
              className='aes-ai-chat-close'
              title='Close'
              aria-label='Close AES Assistant'
            >
              ×
            </button>
          </div>

          <div className='aes-ai-chat-messages' style={styles.messages}>
            {messages.length > 0 && !loading && (
              <div className='aes-ai-chat-clear-row'>
                <button type='button' className='aes-ai-chat-clear' onClick={clearConversation}>
                  Clear conversation
                </button>
              </div>
            )}

            {messages.length === 0 && (
              <div style={styles.empty}>
                <h2 style={styles.emptyTitle}>How can I help?</h2>
                <p style={styles.emptyBody}>
                  Ask about AES projects, jobs, documents, services, or company information.
                  <br />
                  Type @ to find and insert a project.
                </p>
                <div className='aes-ai-chat-suggestions'>
                  {STARTER_ACTIONS.map((suggestion) => (
                    <button
                      key={suggestion.label}
                      type='button'
                      className='aes-ai-chat-suggestion'
                      style={styles.suggestion}
                      onClick={() => applySuggestion(suggestion.prompt, suggestion.opensProjectPicker)}
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message, index) =>
              message.role === 'user' ? (
                <div key={index} className='aes-ai-chat-messages-user' style={styles.userMessage}>
                  {message.content}
                </div>
              ) : (
                <AssistantResponseLayout
                  key={index}
                  cardStyle={message.disabled ? styles.disabledMessage : styles.responseMessage}
                  copyContent={message.content}
                  showCopy={shouldShowResponseCopy(message)}
                  resources={(hasApplicationActions(message.actions) || hasSources(message.sources)) ? (
                    <>
                      <ApplicationActions actions={message.actions} />
                      <AssistantSources sources={message.sources} openingSourceKey={openingSourceKey} onOpenSource={openSource} />
                    </>
                  ) : null}
                >
                    {message.disabled ? (
                      message.content
                    ) : (
                      <>
                        <AnswerContent text={message.content} sources={message.sources} />
                        {message.clarification?.options?.length > 0 && messages.slice(index + 1).every((next) => next.role !== 'assistant' || next.transient) && (
                          <AssistantClarificationChoices
                            options={message.clarification.options}
                            disabled={loading}
                            onSelect={handleSend}
                          />
                        )}
                      </>
                    )}
                </AssistantResponseLayout>
              ),
            )}

            {loading && (
              <div className='aes-ai-chat-thinking' role='status' aria-label='AES Assistant is thinking'>
                <span aria-hidden='true' />
                <span aria-hidden='true' />
                <span aria-hidden='true' />
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          <div className='aes-ai-chat-input-row' style={styles.inputRow}>
            <div ref={composerRef} className='aes-ai-chat-composer' onClick={handleComposerClick}>
              {projectMention && (
                <div
                  ref={projectPickerRef}
                  id='aes-ai-chat-project-picker'
                  className='aes-ai-chat-project-picker'
                  role='listbox'
                  aria-label='Project suggestions'
                >
                  <div className='aes-ai-chat-project-picker-header'>
                    {projectMention.query ? `Projects matching “${projectMention.query}”` : 'Recent AES projects'}
                  </div>

                  {projectSearchLoading ? (
                    <div className='aes-ai-chat-project-picker-message'>Searching projects…</div>
                  ) : projectSearchError ? (
                    <div className='aes-ai-chat-project-picker-message'>Unable to load projects.</div>
                  ) : projectResults.length ? (
                    projectResults.map((project, index) => {
                      const projectMetadata = [project.clientName, project.locationName, project.jobStatus].filter(Boolean).join(' · ');

                      const handleProjectSelection = (event) => {
                        event.preventDefault();
                        selectProject(project);
                      };

                      return (
                        <button
                          key={project.proposalId}
                          type='button'
                          className={`aes-ai-chat-project-result${index === highlightedProjectIndex ? ' is-active' : ''}`}
                          role='option'
                          aria-selected={index === highlightedProjectIndex}
                          data-project-index={index}
                          onMouseEnter={() => setHighlightedProjectIndex(index)}
                          onMouseDown={handleProjectSelection}
                          onTouchStart={handleProjectSelection}
                        >
                          <span className='aes-ai-chat-project-number'>{project.projectNumber}</span>
                          {project.projectName && <span className='aes-ai-chat-project-name'>{project.projectName}</span>}
                          {projectMetadata && <span className='aes-ai-chat-project-meta'>{projectMetadata}</span>}
                        </button>
                      );
                    })
                  ) : (
                    <div className='aes-ai-chat-project-picker-message'>No matching projects found.</div>
                  )}
                </div>
              )}

              <textarea
                ref={textareaRef}
                className='aes-ai-chat-input'
                style={styles.input}
                value={input}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                onKeyUp={handleTextareaKeyUp}
                onClick={handleTextareaClick}
                placeholder='Ask AES Assistant'
                aria-label='Ask AES Assistant'
                aria-controls='aes-ai-chat-project-picker'
                rows={1}
              />

              <div className='aes-ai-chat-composer-footer'>
                <span className='aes-ai-chat-input-hint'>
                  <span>@Project</span>
                  <span>|</span>
                  <span>
                    <span className='keyboard-key mr-2'>Enter</span> to send
                  </span>
                  <span>|</span>
                  <span>
                    <span className='keyboard-key mr-1'>Shift</span> + <span className='keyboard-key ml-1 mr-2'>Enter</span> for new line
                  </span>
                </span>
                <button
                  className='aes-ai-chat-send'
                  style={styles.sendBtn}
                  onClick={handleSend}
                  onMouseEnter={(event) => {
                    if (!event.currentTarget.disabled) {
                      event.currentTarget.style.background = PRIMARY_HOVER;
                    }
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.background = PRIMARY;
                  }}
                  disabled={!canSendChatMessage(input, loading)}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default connect(null, { ...alert.actions })(AiChatPanel);
